import { sitemap } from 'app/shared/util/url-utils';
import type { ReactNode } from 'react';
import React from 'react';
import {
  HomeOutlined,
  Business,
  Forum,
  RecordVoiceOver,
  Source,
  Newspaper,
  Gavel,
  SettingsOutlined,
  ListAltOutlined,
  BoltOutlined,
} from '@mui/icons-material';

import { Translate } from 'react-jhipster';
import { useListAccountSubscriptionsConfigManagerCloudServicesApiV1SubscriptionsCurrentGetQuery } from 'app/store/reducers/api/config-manager-cloud-services/store/subscriptions';
import { useListProductCatalogConfigManagerCloudServicesApiV1ProductCatalogGetQuery } from 'app/store/reducers/api/config-manager-cloud-services/store/product-catalog';
import { useCurrentAccountPermissions } from 'app/shared/restricted/hooks/usePermission';

export type RouteType = {
  element: ReactNode;
  state: string;
  index?: boolean;
  path?: string;
  child?: RouteType[];
  permission?: string;
  /** Stay highlighted on sub-pages of `path`, not only on an exact match. */
  matchPrefix?: boolean;
  sidebarProps?: {
    displayText: string;
    icon?: ReactNode;
  };
};

/** Hide a nav item (recursively) when the active account lacks its required permission. */
const filterNavByPermission = (routes: any[], has: (permission?: string) => boolean): any[] =>
  routes
    .map(route => (route.child ? { ...route, child: filterNavByPermission(route.child, has) } : route))
    .filter(route => (route.child ? route.child.length > 0 : has(route.permission)));

const appRoutes = () => {
  const { currentData: productCatalog = [] } =
    useListProductCatalogConfigManagerCloudServicesApiV1ProductCatalogGetQuery();
  const { currentData: subscriptions = [] } =
    useListAccountSubscriptionsConfigManagerCloudServicesApiV1SubscriptionsCurrentGetQuery();
  const getSubscriptionByProductName = (name: string) => {
    const product = productCatalog.find(p => p.name?.toLowerCase() === name.toLowerCase());
    if (!product) return undefined;
    return subscriptions.find(s => s.productCatalogId === product.id && s.status === 'ACTIVE');
  };

  const chatbotsSubscribed = getSubscriptionByProductName('chatbots');
  const speak2meSubscribed = getSubscriptionByProductName('speak2me');
  const newslettersSubscribed = getSubscriptionByProductName('newsletters');
  const leadgenSubscribed = getSubscriptionByProductName('lead generation');
  const tendersSubscribed = getSubscriptionByProductName('tenders');

  const permissions = useCurrentAccountPermissions();
  const has = (permission?: string) => !permission || permissions.includes(permission);

  const routes = [
    {
      path: sitemap.home,
      sidebarProps: {
        displayText: <Translate contentKey="module-factory.home">Home</Translate>,
        icon: <HomeOutlined />,
      },
    },
    {
      path: 'data-manager',
      sidebarProps: {
        displayText: 'Data Manager',
        icon: <Source />,
      },
      child: [
        {
          path: sitemap.dataManager.datasources,
          permission: 'datasource:read',
          sidebarProps: {
            displayText: 'Data Sources',
          },
        },
        // {
        //   path: sitemap.dataManager.sources,
        //   sidebarProps: {
        //     displayText: 'Sources',
        //   },
        // },
        {
          path: sitemap.dataManager.collections,
          permission: 'collection:read',
          sidebarProps: {
            displayText: 'Collections',
          },
        },
        {
          path: sitemap.dataManager.pipelines,
          permission: 'pipeline:read',
          sidebarProps: {
            displayText: 'Pipelines',
          },
        },
      ],
    },
    ...(chatbotsSubscribed
      ? [
          {
            path: 'chatbots',
            sidebarProps: {
              displayText: <Translate contentKey="module-factory.chatbots">Chatbots</Translate>,
              icon: <Forum />,
            },
            child: [
              {
                path: sitemap.chatbots['chat-profiles'],
                permission: 'chat_profile:read',
                sidebarProps: {
                  displayText: <Translate contentKey="module-factory.chat-profiles">Chat Profiles</Translate>,
                },
              },
              {
                path: sitemap.chatbots.preview,
                sidebarProps: {
                  displayText: <Translate contentKey="module-factory.preview">Preview</Translate>,
                },
              },
            ],
          },
        ]
      : []),

    ...(speak2meSubscribed
      ? [
          {
            path: 'speak2me',
            sidebarProps: {
              displayText: 'Speak 2 Me',
              icon: <RecordVoiceOver />,
            },
            child: [
              {
                path: sitemap.speak2me.transcription,
                sidebarProps: {
                  displayText: <Translate contentKey="module-factory.transcription">Transcription</Translate>,
                },
              },
              {
                path: sitemap.speak2me.translation,
                sidebarProps: {
                  displayText: <Translate contentKey="module-factory.translation">Translation</Translate>,
                },
              },
            ],
          },
        ]
      : []),

    ...(newslettersSubscribed
      ? [
          {
            path: 'newsletters',
            sidebarProps: {
              displayText: 'NewsLetters',
              icon: <Newspaper />,
            },
            child: [
              {
                path: sitemap.newsletters.newsLetters,
                sidebarProps: {
                  displayText: 'NewsLetters',
                },
              },
              // {
              //   path: sitemap.newsletters.todaysOpportunities,
              //   sidebarProps: {
              //     displayText: "Today's Opportunities",
              //   },
              // },
            ],
          },
        ]
      : []),

    // Appels d'Offres. No permission gate yet.
    ...(tendersSubscribed
      ? [
          {
            path: 'tenders',
            // Open Opportunities (not the first child) when clicking the product.
            to: sitemap.tenders.opportunities,
            sidebarProps: {
              displayText: <Translate contentKey="tenders.nav.title">Tenders</Translate>,
              icon: <Gavel />,
            },
            child: [
              {
                path: sitemap.tenders.opportunities,
                // Stay selected on `/opportunities/import` and `/opportunities/:reference`.
                matchPrefix: true,
                sidebarProps: {
                  displayText: <Translate contentKey="tenders.nav.opportunities">Call For Tenders</Translate>,
                  icon: <ListAltOutlined fontSize="small" />,
                },
              },
              {
                path: sitemap.tenders.actions,
                sidebarProps: {
                  displayText: <Translate contentKey="tenders.nav.actions">Actions</Translate>,
                  icon: <BoltOutlined fontSize="small" />,
                },
              },
              {
                path: 'tenders/settings',
                to: sitemap.tenders.watchCriteria,
                sidebarProps: {
                  displayText: <Translate contentKey="tenders.nav.settings">Settings</Translate>,
                  icon: <SettingsOutlined fontSize="small" />,
                },
                child: [
                  {
                    path: sitemap.tenders.watchCriteria,
                    sidebarProps: {
                      displayText: <Translate contentKey="tenders.nav.watchCriteria">Watch criteria</Translate>,
                    },
                  },
                  {
                    path: sitemap.tenders.organization,
                    sidebarProps: {
                      displayText: <Translate contentKey="tenders.nav.myOrganization">My organisation</Translate>,
                    },
                  },
                  {
                    path: sitemap.tenders.platformAccounts,
                    sidebarProps: {
                      displayText: <Translate contentKey="tenders.nav.platformAccounts">Platform accounts</Translate>,
                    },
                  },
                ],
              },
            ],
          },
        ]
      : []),

    ...(leadgenSubscribed
      ? [
          {
            path: sitemap.leadgeneration.company,
            sidebarProps: {
              displayText: <Translate contentKey="module-factory.lead-generation">Lead Generation</Translate>,
              icon: <Business />,
            },
          },
        ]
      : []),
  ];

  return filterNavByPermission(routes, has);
};

export default appRoutes;
